import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import * as Sentry from '@sentry/react';
import alarmSound from 'src/Sounds/error.mp3';
import { parseTsToMillis } from '../utils/matchEventsFeed';
import type { SniperReversal } from './useSniperLiveSignals';

/**
 * How current a reversal must be to alarm. Sized to cover a normal live delay
 * (feed → engine → relay → socket) with slack for clock skew, while being far
 * short of "the operator just reopened the page".
 */
export const REVERSAL_ALARM_MAX_AGE_MS = 90_000;

/**
 * One stale-suppression report per session. A skewed clock suppresses EVERY
 * reversal, so without this the report would repeat for the whole session.
 * Module-level, and reset by {@link __resetStaleReportForTests}.
 */
let staleReportSent = false;

/** Test-only: clear the once-per-session suppression-report latch. */
export function __resetStaleReportForTests(): void {
  staleReportSent = false;
}

/**
 * Plays the ALARM cue when a goal is retracted / reversed on any fixture in the
 * open Sniper group.
 *
 * Why this exists instead of routing through `useNotificationSound`: sniper ops
 * are `XADD`ed to `sniper:notifications` and reach the frontend ONLY as
 * `match_operation` frames on the `/match-events` namespace. The app-wide sound
 * mount (`PredictionNotificationSound`) is fed by `useNotificationsSocket`,
 * which reads the ROOT `notifications:stream` — sniper types never land there,
 * so a sniper entry in `useNotificationSound`'s tier sets could never ring on
 * its own. Because of that, `sniper_var_reversal` / `sniper_goal_cancelled` /
 * `sniper_fire_blocked` were REMOVED from `ALARM_EVENT_TYPES` when it was
 * narrowed to a five-type whitelist (2026-08-07) — they were unreachable
 * vocabulary, and their removal changes nothing here. This hook is, and always
 * was, the only thing that makes the operator hear a reversal.
 *
 * The gate is RECENCY, not novelty. `subscribe_match` replays the durable
 * `match_operations` on EVERY subscribe, so on a fresh page load the newest
 * historical reversal arrives looking exactly like a live one — identity alone
 * would alarm on every refresh for an annulment that happened hours ago. An
 * alarm that cries an hour late is worse than no alarm: it teaches the operator
 * to ignore the cue. So a reversal rings only while it is still current, and the
 * key dedupe on top of that keeps a re-render from replaying it.
 *
 * `now` is injectable for tests only.
 */
export function useSniperReversalSound(
  reversal: SniperReversal | null,
  now: () => number = Date.now,
): void {
  const { load } = useAudioPlayer();
  const played = useRef<string | null>(null);
  const nowRef = useRef(now);
  nowRef.current = now;

  useEffect(() => {
    if (reversal == null || reversal.key === played.current) return;
    // Mark it seen whether or not it rings, so a stale reversal can never
    // become ringable later just because something else re-rendered the page.
    played.current = reversal.key;
    const age = nowRef.current() - parseTsToMillis(reversal.ts);
    // An unparseable stamp parses to -Infinity, so `age` is +Infinity and fails
    // closed — silent. A NEGATIVE age (our clock trailing the engine's) is
    // current, not stale, so only the upper bound is checked.
    if (!Number.isFinite(age) || age > REVERSAL_ALARM_MAX_AGE_MS) {
      // Suppression is measured against the browser clock, so a machine running
      // more than the window AHEAD of the engine would silence every live
      // reversal — and silence is indistinguishable from "no reversal". Report
      // it as a real captured event, ONCE per session: a breadcrumb is only
      // transmitted attached to some other captured exception, so in the common
      // case (a clean session) it would never be sent anywhere and the
      // suppression would stay exactly as invisible as before.
      // (The principled fix is to gate the live path on the gateway's
      // `operations_backfill_complete` marker rather than on wall-clock age.)
      if (!staleReportSent) {
        staleReportSent = true;
        Sentry.captureMessage('reversal alarm suppressed as stale', {
          level: 'warning',
          tags: { area: 'sniper-reversal-sound' },
          extra: { key: reversal.key, ts: reversal.ts, ageMs: age },
        });
      }
      return;
    }
    try {
      load(alarmSound, { autoplay: true, loop: false });
    } catch {
      /* audio unavailable — the reorder + row still surface the reversal */
    }
  }, [reversal, load]);
}
